import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  GetStylesFunction,
  IconHeaderWithSearch,
  LoadingSpinner,
  asQueryString,
  enumContainer,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFiltersPane,
  TableSorting,
  UserFilterDto,
  WallabyCoreSchema,
  useFormatters,
  useOrganizationsByOrgSecurity,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';

import {DepartmentReportFilters} from './DepartmentReportLayoutContainer';
import {HospitalReportTableAndCharts, HospitalReportData} from './HospitalReportTableAndCharts';
import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {useAppSelector} from 'src/js/store_hooks';

export const HaiTypeCategory = [
  {name: 'HAI_TYPE', messageKey: 'enum.haiTypeCategory.haiType'},
  {name: 'HAI_STANDARD_TYPE_UPPER', messageKey: 'enum.haiTypeCategory.haiStandardTypeUpper'},
  {name: 'HAI_STANDARD_TYPE_LOWER', messageKey: 'enum.haiTypeCategory.haiStandardTypeLower'},
];

// NOTE: fallback so the Aggregation filter renders its value on first paint instead of
// waiting for the async enumMap (HaiReportMeasureType) to load (PDH-2840).
export const HaiReportMeasureType = [
  {name: 'HAI_COUNT', messageKey: 'enum.haiReportMeasureType.haiCount'},
  {name: 'HOSP_WITH_HAI_COUNT', messageKey: 'enum.haiReportMeasureType.hospWithHaiCount'},
];

type HospitalReportFilters = {
  dateFrom: string;
  dateTo: string;
  floatingShortcutName: ShortcutEnum | null;
  haiState: string;
  orgLevelType: string;
  organizationId: string;
  typeCategory: string;
  measureType: 'HAI_COUNT' | 'HOSP_WITH_HAI_COUNT';
};
type FiltersData = {
  reportOpen: boolean;
  report: HospitalReportFilters;
  reportSorting: TableSorting;
};
type DepartmentReportLinkFilters = {
  report: DepartmentReportFilters;
};
const DEFAULT_HOSPITAL_REPORT_FILTERS_OPEN = true;
const DEFAULT_HOSPITAL_REPORT_FILTERS = (organizations: WallabyCoreSchema.components['schemas']['OrganizationFilterDto'][]) => {
  const firstHospital = getDefaultHospital(organizations);
  const parentDepartmentCount = organizations.filter((v) => v.levelType === 'PARENT_DEPARTMENT').length;
  const orgLevelTypeFilter = parentDepartmentCount > 0 ? 'PARENT_DEPARTMENT' : 'DEPARTMENT';
  const [startOfLastMonth, endOfLastMonth] = DateUtils.lastCompletedMonth();
  return {
    dateFrom: startOfLastMonth,
    dateTo: endOfLastMonth,
    floatingShortcutName: ShortcutEnum.PREVIOUS_MONTH,
    haiState: 'ALL',
    orgLevelType: orgLevelTypeFilter, // NOTE: user filter api requires orgLevelType here
    organizationId: firstHospital?.id && String(firstHospital?.id),
    typeCategory: 'HAI_TYPE',
    measureType: 'HAI_COUNT',
  } as HospitalReportFilters;
};
export const DEFAULT_HOSPITAL_REPORT_SORTING = {
  field: 'totalRowStatistics.haiCount',
  direction: 'desc',
};
type Organization = WallabyCoreSchema.components['schemas']['OrganizationFilterDto'];
export function getIsMultiHospital(organizations: Organization[]): boolean {
  return organizations.some((v) => v.levelType === 'GROUP');
}
export function getDefaultHospital(organizations: Organization[]): Organization | undefined {
  return organizations.find((v) => v.levelType === 'HOSPITAL');
}
interface Props {
  config: ConfigObjectType;
}
export function normalizeHaiHospitalReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}
export const HospitalReportLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'hospitalReport';
  const storageIdPrefix = '/haiByDepartments';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const defaultReportFilters = DEFAULT_HOSPITAL_REPORT_FILTERS(organizations);
  const filters: FiltersData = useMemo(() => {
    return {
      reportOpen: query?.reportOpen ?? DEFAULT_HOSPITAL_REPORT_FILTERS_OPEN,
      report: {
        ...defaultReportFilters,
        ...(query?.report ?? {}),
      },
      reportSorting: query?.reportSorting ?? DEFAULT_HOSPITAL_REPORT_SORTING,
    };
  }, [query, defaultReportFilters]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HospitalReportLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
HospitalReportLayoutContainer.displayName = 'HospitalReportLayoutContainer';

type OrgLevelOption = {
  id: string;
  label: string;
};
type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
const HospitalReportLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = hospitalReportGetStyles({theme});

  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const isMultiHospital = getIsMultiHospital(organizations);
  const haveDefaultFilters = organizations.length > 0;
  const defaultReportFilters = DEFAULT_HOSPITAL_REPORT_FILTERS(organizations);
  // get data
  const pageTitle = 'navigation.route.haiByDepartments';
  const reportUrl = config.service.url.singleDepartmentReport;
  const reportParams = useTableParams(
    {
      dateFrom: filters.report.dateFrom,
      dateTo: filters.report.dateTo,
      floatingShortcutName: filters.report.floatingShortcutName,
      haiState: filters.report.haiState,
      orgLevelType: filters.report.orgLevelType,
      organizationId: filters.report.organizationId,
      typeCategory: filters.report.typeCategory,
      measureType: filters.report.measureType,
    }
    // filters.reportSorting, // NOTE: we sort only on FE
    // {}
  );
  const {data: reportData, isLoading} = useTableData<any, HospitalReportData>(reportUrl, reportParams, 'content', haveDefaultFilters);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(
    normalizeHaiHospitalReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeHaiHospitalReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const hasSorting = !isEqual(omitNullsy(filters.reportSorting), omitNullsy(DEFAULT_HOSPITAL_REPORT_SORTING));
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const haiReportsStateOptions = enumMap.HaiReportsState ?? [];
  const orgLevelTypeOptions = useMemo<OrgLevelOption[]>(() => {
    const organizationId = filters.report.organizationId != null ? +filters.report.organizationId : null;
    const orgLevelOptions: OrgLevelOption[] = [];
    const hospitalParentDepartmentCount = organizations.filter(
      (v) => v.levelType === 'PARENT_DEPARTMENT' && v.parentId === organizationId
    ).length;
    if (hospitalParentDepartmentCount > 0) {
      orgLevelOptions.push({
        id: 'PARENT_DEPARTMENT',
        label: tEnum('OrganizationLevelTypeEnum', 'PARENT_DEPARTMENT'),
      });
    }
    orgLevelOptions.push({
      id: 'DEPARTMENT',
      label: tEnum('OrganizationLevelTypeEnum', 'DEPARTMENT'),
    });
    return orgLevelOptions;
  }, [organizations, tEnum, filters.report.organizationId]);
  const hospitalOptions = (organizations ?? [])
    .filter((v) => v.levelType === 'HOSPITAL')
    .map((v) => ({
      id: v.id,
      label: v.organizationLongName,
    }));
  // scroll
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  useEffect(() => {
    const organizationId = filters.report.organizationId != null ? +filters.report.organizationId : null;
    const hospitalParentDepartmentCount = organizations.filter(
      (v) => v.levelType === 'PARENT_DEPARTMENT' && v.parentId === organizationId
    ).length;
    if (hospitalParentDepartmentCount === 0 && filters.report.orgLevelType !== 'DEPARTMENT') {
      changeFilters({
        report: {...filters.report, orgLevelType: 'DEPARTMENT'},
      });
    }
  }, [organizations, filters.report, changeFilters]);
  const isOrgLevelsFilterDisabled = orgLevelTypeOptions.length === 1;
  // The global default (PARENT_DEPARTMENT when any hospital has parent departments) is not a valid option for a
  // hospital without them: the effect above forces DEPARTMENT there, so the chip must treat DEPARTMENT as the
  // default too, otherwise it offers a cross that "resets" to a value the effect immediately reverts.
  const defaultOrgLevelType = orgLevelTypeOptions.some((v) => v.id === defaultReportFilters.orgLevelType)
    ? defaultReportFilters.orgLevelType
    : 'DEPARTMENT';
  const haiTypeCategoryOptions = enumMap.HaiTypeCategory ?? HaiTypeCategory;
  const haiMeasureTypeOptions = enumMap.HaiReportMeasureType ?? HaiReportMeasureType;
  const {standardType} = useStandardTypes({config});

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('reports.filters.date'),
          value: `${formatDate(filters.report.dateFrom)} - ${formatDate(filters.report.dateTo)}`,
          defaultValue: [defaultReportFilters.dateFrom, defaultReportFilters.dateTo, defaultReportFilters.floatingShortcutName],
        },
        {
          name: 'haiState',
          label: t('reports.filters.haiState'),
          value: tEnum('HaiReportsState', filters.report.haiState),
          defaultValue: defaultReportFilters.haiState,
        },
        ...(isMultiHospital
          ? [
              {
                name: 'organizationId',
                label: t('reports.filters.organizationId'),
                value: hospitalOptions.find((w) => String(w.id) === String(filters.report.organizationId))?.label ?? '?',
                defaultValue: defaultReportFilters.organizationId,
              },
            ]
          : []),
        {
          name: 'orgLevelType',
          label: t('reports.filters.orgLevelType'),
          value: orgLevelTypeOptions.find((v) => v.id === filters.report.orgLevelType)?.label ?? '?',
          defaultValue: defaultOrgLevelType,
        },
        {
          name: 'typeCategory',
          label: t('reports.filters.haiTypeCategory'),
          value: tEnum('HaiTypeCategory', filters.report.typeCategory),
          defaultValue: defaultReportFilters.typeCategory,
        },
        {
          name: 'measureType',
          label: t('reports.filters.haiMeasureType'),
          value: tEnum('HaiReportMeasureType', filters.report.measureType),
          defaultValue: defaultReportFilters.measureType,
        },
      ]}
      tableFilters={filters.report}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          report: newTableFilters,
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('reports.list.pageTitle')} | ${t(pageTitle)}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.reportOpen}
          toggleOpen={() =>
            changeFilters({
              reportOpen: !filters.reportOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.report,
            setFilter: (newFilter) => {
              changeFilters({
                report: newFilter,
              });
            },
            config,
            normalizeForCompare: normalizeHaiHospitalReportFiltersForCompare,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightMonthRangeFilter
            config={{
              name: 'date',
              ui: {label: 'reports.filters.date'},
            }}
            value={{
              startDate: filters.report.dateFrom,
              endDate: filters.report.dateTo,
              shortcut: filters.report.floatingShortcutName,
            }}
            defaultValue={
              defaultReportFilters.dateFrom && defaultReportFilters.dateTo
                ? {
                    startDate: defaultReportFilters.dateFrom,
                    endDate: defaultReportFilters.dateTo,
                    shortcut: defaultReportFilters.floatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  dateFrom: newValue?.startDate,
                  dateTo: newValue?.endDate,
                  floatingShortcutName: newValue?.shortcut,
                },
              });
            }}
            showShortcuts
          />
          <LightSelectFilter
            config={{
              name: 'haiState',
              ui: {label: 'reports.filters.haiState'},
            }}
            options={haiReportsStateOptions}
            value={filters.report.haiState}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiState: newValue ?? defaultReportFilters.haiState,
                },
              });
            }}
            isClearable={false}
          />
          {isMultiHospital && (
            <LightSelectFilter
              config={{
                name: 'organizationId',
                ui: {label: 'reports.filters.organizationId'},
              }}
              options={hospitalOptions}
              value={filters.report.organizationId}
              onChange={(newOrganizationId) => {
                changeFilters({
                  report: {
                    ...filters.report,
                    organizationId: newOrganizationId,
                  },
                });
              }}
              isClearable={false}
            />
          )}
          <LightSelectFilter
            config={{
              name: 'orgLevelType',
              ui: {label: 'reports.filters.orgLevelType'},
            }}
            options={orgLevelTypeOptions}
            value={filters.report.orgLevelType}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  orgLevelType: newValue ?? defaultOrgLevelType,
                },
              });
            }}
            disabled={isOrgLevelsFilterDisabled}
          />
          {standardType !== 'NO_TYPES' && (
            <LightSelectFilter
              config={{
                name: 'haiTypeCategory',
                ui: {label: 'reports.filters.haiTypeCategory'},
              }}
              options={haiTypeCategoryOptions}
              value={filters.report.typeCategory}
              onChange={(newValue) => {
                changeFilters({
                  report: {
                    ...filters.report,
                    typeCategory: newValue ?? defaultReportFilters.typeCategory,
                  },
                });
              }}
              isClearable={false}
            />
          )}
          <LightSelectFilter
            config={{
              name: 'haiMeasureType',
              ui: {label: 'reports.filters.haiMeasureType'},
            }}
            options={sortByMessageKey(haiMeasureTypeOptions ?? []).reverse()}
            value={filters.report.measureType}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  measureType: newValue ?? defaultReportFilters.measureType,
                },
              });
            }}
            isClearable={false}
          />
        </LightFilterPanel>
      </div>
      <HospitalReportTableAndCharts
        departmentTableAndChartLabel='reports.haiCountPerDepartment'
        departmentColumnLabel='reports.haiCountPerDepartment.departmentName'
        getReportLink={(data) => {
          const {departmentId} = data;
          const linkFilters: DepartmentReportLinkFilters = {
            report: {
              dateFrom: filters.report.dateFrom,
              dateTo: filters.report.dateTo,
              floatingShortcutName: filters.report.floatingShortcutName,
              haiState: filters.report.haiState,
              organizationIds: null,
              typeCategory: filters.report.typeCategory,
              measureType: filters.report.measureType,
            },
          };
          if (departmentId != null) linkFilters.report.organizationIds = [String(departmentId)];
          if (!data.isTotalRow) return `/departmentReport?${asQueryString(linkFilters)}`;
          return undefined;
        }}
        muiStyles={muiStyles}
        storageIdPrefix={storageIdPrefix}
        filters={filters}
        changeFilters={changeFilters}
        hasFilters={hasFilters}
        handleResetFilters={() => changeFilters({report: defaultReportFilters})}
        hasSorting={hasSorting}
        handleResetSorting={() =>
          changeFilters({
            reportSorting: DEFAULT_HOSPITAL_REPORT_SORTING,
          })
        }
        formatters={formatters}
        isLoading={isLoading}
        reportData={reportData}
        config={config}
        reportName='HAI_SINGLE_DEPARTMENT_REPORT'
      />
    </div>
  );
};

export const hospitalReportGetStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: scroll;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    flex: 0 0 auto;
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
    color: var(--dlTheme-700);
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
